import React, { useEffect, useMemo } from 'react';
import queryString from 'query-string';
import Flashbar, {
	FlashbarProps,
} from '@amzn/awsui-components-console/flashbar';
import Box from '@amzn/awsui-components-console/box';
import Header from '@amzn/awsui-components-console/header';
import Spinner from '@amzn/awsui-components-console/spinner';
import { GetWorkflowDefinitionResponse } from '@amzn/snow-nxt-workflow-service-typescript-client';
import { v4 as uuidv4 } from 'uuid';

import { SNOW_NXT_WORKFLOW_ENDPOINT } from 'src/config';
import { useFetchForSDS } from 'src/hooks/useFetchForSDS';

import './FindEntity.scss';
import CycleStatusComponent from '../../components/findEntity/CycleStatus';

interface FindCycleLocationState {
	cycleData?: Record<string, unknown> | null;
}

interface FindCycleProps {
	location: {
		search: string;
		state?: FindCycleLocationState;
		[key: string]: unknown;
	};
	hideTools: () => void;
	[key: string]: unknown;
}

export default function FindCycle({ location, hideTools }: FindCycleProps) {
	const parsed = queryString.parse(location.search);
	const cycleId = (parsed.cycleId as string) || '';
	const workflowType = (parsed.workflowType as string) || '';
	const cycleData = location.state?.cycleData || null;

	useEffect(() => {
		hideTools();
	}, [hideTools]);

	const workflowDefinitionURL = workflowType ? `/workflows/${workflowType}` : '';

	const { data: workflowDefinitionData, isLoading } = useFetchForSDS<GetWorkflowDefinitionResponse>({
		path: workflowDefinitionURL,
		method: 'GET',
		url: SNOW_NXT_WORKFLOW_ENDPOINT,
	});

	const workflowDefinitionName =
		workflowDefinitionData?.workflowDefinition?.workflowDefinitionName || workflowType;

	const flashBarItems = useMemo<FlashbarProps.MessageDefinition[]>(() => {
		if (!cycleId || !workflowType) {
			return [{
				id: uuidv4(),
				header: 'Missing parameters',
				type: 'error',
				content: 'Both Cycle ID and Workflow Type are required.',
				dismissible: true,
			}];
		}
		return [];
	}, [cycleId, workflowType]);

	if (isLoading) {
		return (
			<Box textAlign="center" padding="xxl">
				<Spinner size="large" />
			</Box>
		);
	}

	if (!cycleId || !workflowType) {
		return (
			<Box>
				<Flashbar items={flashBarItems} />
				<Box padding="xxl" textAlign="center">
					<Header variant="h2">Execution Not Found</Header>
					<Box margin={{ top: 's' }}>
						Please check the Cycle ID and Workflow Type!
					</Box>
				</Box>
			</Box>
		);
	}

	return (
		<CycleStatusComponent
			cycleId={cycleId}
			workflowType={workflowType}
			workflowDefinitionName={workflowDefinitionName}
			cycleData={cycleData}
		/>
	);
}
